import type { Ref } from 'vue';
import { extractApiErrorMessage, useCustomToast } from '@/shared/lib';
import { trainingApi } from '@/entities/Training';

export type CurriculumUploadStatus = 'idle' | 'uploading' | 'completed';

export interface CurriculumUploadRow {
  curriculumObjectName: string | null;
  curriculumFileName: string | null;
  uploadProgress: number;
  uploadStatus: CurriculumUploadStatus;
}

export function useCurriculumUploadRow<TRow extends CurriculumUploadRow>(rows: Ref<TRow[]>) {
  const { showErrorToast } = useCustomToast();

  async function selectCurriculumFile(index: number, file: File): Promise<void> {
    const row = rows.value[index];
    if (!row) return;

    row.curriculumFileName = file.name;
    row.uploadStatus = 'uploading';
    row.uploadProgress = 0;

    try {
      const response = await trainingApi.uploadInstructorCurriculum(file, {
        onUploadProgress: (event) => {
          if (!event.total) return;
          row.uploadProgress = Math.round((event.loaded * 100) / event.total);
        },
      });
      row.curriculumObjectName = response.data.curriculumObjectName;
      row.uploadProgress = 100;
      row.uploadStatus = 'completed';
    } catch (error) {
      row.curriculumObjectName = null;
      row.curriculumFileName = null;
      row.uploadProgress = 0;
      row.uploadStatus = 'idle';
      showErrorToast({
        title: 'Erro ao enviar currículo. Tente novamente',
        message: extractApiErrorMessage(error),
        type: 'error',
      });
    }
  }

  function removeCurriculumFile(index: number): void {
    const row = rows.value[index];
    if (!row) return;
    row.curriculumObjectName = null;
    row.curriculumFileName = null;
    row.uploadProgress = 0;
    row.uploadStatus = 'idle';
  }

  return { selectCurriculumFile, removeCurriculumFile };
}
